import { useState, useRef, useEffect, useCallback } from 'react';
import { useSnackbar } from '../../contexts/SnackbarProvider';
import { useOriginAccess } from '../../hooks/useOriginAccess';
import { SnackbarType } from '../Snackbar/types';
import { MATERIAL_UPLOAD_INTERNAL_ONLY } from '../../constants/accessMessages';
import { isInternalNetworkOnlyError } from '../../utils/internalNetworkError';
import {
  fetchVideoMaterials,
  createVideoMaterial,
  deleteVideoMaterial,
} from '../../services/videoMaterialApi/videoMaterialApi';
import {
  VideoEditMaterialsSectionProps,
  UseVideoEditMaterialsSectionReturn,
  VideoMaterial,
  MAX_MATERIALS_PER_VIDEO,
  MAX_MATERIAL_FILE_SIZE_BYTES,
} from './types';

export const formatFileSize = (bytes: number): string => {
  if (!bytes || bytes <= 0) return '0KB';
  const kb = bytes / 1024;
  if (kb < 1024) return `${Math.max(1, Math.round(kb))}KB`;
  return `${(kb / 1024).toFixed(1)}MB`;
};

/**
 * VideoEditMaterialsSection のビジネスロジックを管理するカスタムフック
 *
 * 画像セクション（VideoEditImagesSection）と異なり、資料は追加・削除を
 * その場で確定するシンプルな設計にしている（保存ボタンでの一括確定は行わない）。
 * PDF 1〜3枚・並び替え不要という要件の小ささに対して、一時保存の仕組みは
 * 複雑さに見合わないと判断した。
 */
export const useVideoEditMaterialsSection = (
  props: VideoEditMaterialsSectionProps
): UseVideoEditMaterialsSectionReturn => {
  const { videoId, isVideoProcessing } = props;
  const showSnackbar = useSnackbar();
  const { isExternal } = useOriginAccess();

  const [materials, setMaterials] = useState<VideoMaterial[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isUploading, setIsUploading] = useState<boolean>(false);
  const [isDragging, setIsDragging] = useState<boolean>(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const canLoad = !!videoId && !videoId.startsWith('session_') && !isVideoProcessing;

  useEffect(() => {
    if (!canLoad) {
      setMaterials([]);
      return;
    }

    let cancelled = false;
    setIsLoading(true);
    fetchVideoMaterials(videoId)
      .then(list => {
        if (!cancelled) setMaterials(list);
      })
      .catch(error => {
        console.error('資料一覧の取得に失敗しました:', error);
        if (!cancelled) showSnackbar('資料一覧の取得に失敗しました', SnackbarType.ERROR);
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [videoId, canLoad]);

  const validateFile = useCallback((file: File): string | null => {
    const lowerName = file.name.toLowerCase();
    if (!lowerName.endsWith('.pdf')) {
      return 'PDF形式のファイルのみ追加できます';
    }
    if (file.size > MAX_MATERIAL_FILE_SIZE_BYTES) {
      return 'ファイルサイズは100MBまでです';
    }
    if (materials.length >= MAX_MATERIALS_PER_VIDEO) {
      return `資料は最大${MAX_MATERIALS_PER_VIDEO}件までです`;
    }
    return null;
  }, [materials.length]);

  const uploadFile = useCallback(async (file: File): Promise<void> => {
    // 院外では画面上も追加を押せないようにしているが（isExternalNetwork でボタン非活性）、
    // ドラッグ&ドロップ等でここまで来た場合に備えて二重に防ぐ
    if (isExternal) {
      showSnackbar(MATERIAL_UPLOAD_INTERNAL_ONLY, SnackbarType.ERROR);
      return;
    }

    const validationError = validateFile(file);
    if (validationError) {
      showSnackbar(validationError, SnackbarType.WARNING);
      return;
    }

    setIsUploading(true);
    try {
      const created = await createVideoMaterial(videoId, file);
      setMaterials(prev => [...prev, created]);
      showSnackbar('資料を追加しました', SnackbarType.SUCCESS);
    } catch (error) {
      console.error('資料の追加に失敗しました:', error);
      // 画面上の isExternal 判定はサーバ取得までの間 false のままになりうるため、
      // 実際に院内限定ゲート(403 + INTERNAL_NETWORK_ONLY)で拒否された場合は
      // 汎用メッセージで潰さず専用文言を出す
      const message = isInternalNetworkOnlyError(error)
        ? MATERIAL_UPLOAD_INTERNAL_ONLY
        : (error as any)?.response?.data?.error || '資料の追加に失敗しました';
      showSnackbar(message, SnackbarType.ERROR);
    } finally {
      setIsUploading(false);
    }
  }, [videoId, validateFile, showSnackbar, isExternal]);

  const handleFileButtonClick = useCallback((): void => {
    fileInputRef.current?.click();
  }, []);

  const handleFileInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>): void => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (file) {
      void uploadFile(file);
    }
  }, [uploadFile]);

  const handleDragOver = useCallback((e: React.DragEvent<HTMLDivElement>): void => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((): void => {
    setIsDragging(false);
  }, []);

  const handleDrop = useCallback((e: React.DragEvent<HTMLDivElement>): void => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files?.[0];
    if (file) {
      void uploadFile(file);
    }
  }, [uploadFile]);

  const handleDelete = useCallback((id: number): void => {
    if (isExternal) {
      showSnackbar(MATERIAL_UPLOAD_INTERNAL_ONLY, SnackbarType.ERROR);
      return;
    }

    const target = materials.find(m => m.id === id);
    // 楽観的に削除し、失敗したら戻す
    setMaterials(prev => prev.filter(m => m.id !== id));
    deleteVideoMaterial(id)
      .then(() => {
        showSnackbar('資料を削除しました', SnackbarType.SUCCESS);
      })
      .catch(error => {
        console.error('資料の削除に失敗しました:', error);
        // 画面上の isExternal 判定が取れないまま院内限定ゲートで拒否された場合、
        // 「資料の削除に失敗しました」だけでは理由が分からない。専用文言を出す
        const message = isInternalNetworkOnlyError(error)
          ? MATERIAL_UPLOAD_INTERNAL_ONLY
          : '資料の削除に失敗しました';
        showSnackbar(message, SnackbarType.ERROR);
        if (target) {
          setMaterials(prev => [...prev, target].sort((a, b) => a.display_order - b.display_order));
        }
      });
  }, [materials, showSnackbar, isExternal]);

  return {
    materials,
    isLoading,
    isUploading,
    fileInputRef,
    isDragging,
    isExternalNetwork: isExternal,
    handleFileButtonClick,
    handleFileInputChange,
    handleDragOver,
    handleDragLeave,
    handleDrop,
    handleDelete,
    formatFileSize,
  };
};
